import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin } from "lucide-react";
import axios from "axios";
import { FadeIn } from "@/components/FadeIn";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_TYPES = ["All", "Dialogue", "Retreat", "Climate Workshops", "Education Programs", "Village Immersion"];

export default function EventsPage() {
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [typeFilter, setTypeFilter] = useState("All");
  const [showAllPast, setShowAllPast] = useState(false);

  useEffect(() => {
    const params = typeFilter !== "All" ? `?event_type=${typeFilter}&status=upcoming` : "?status=upcoming";
    const paramsP = typeFilter !== "All" ? `?event_type=${typeFilter}&status=past` : "?status=past";
    axios.get(`${API}/events${params}`).then((r) => setUpcoming(r.data)).catch(() => {});
    axios.get(`${API}/events${paramsP}`).then((r) => setPast(r.data)).catch(() => {});
  }, [typeFilter]);

  const EventCard = ({ event, index }) => (
    <Link
      to={`/events/${event.slug}`}
      className="group block border border-[hsl(48,12%,88%)] hover:border-[#5C6B5D]/40 transition-colors duration-300 overflow-hidden"
      data-testid={`event-card-${event.slug}`}
    >
      {event.featured_image && (
        <div className="overflow-hidden">
          <img
            src={event.featured_image}
            alt={event.title}
            className="w-full aspect-[16/10] object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-700"
            loading="lazy"
          />
        </div>
      )}
      <div className="p-6 md:p-8">
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge variant="outline" className="font-body text-[10px] tracking-wider uppercase border-[#5C6B5D] text-[#5C6B5D]">
            {event.event_type}
          </Badge>
          {event.contribution_type && (
            <Badge variant="outline" className="font-body text-[10px] tracking-wider uppercase border-[hsl(48,12%,88%)] text-muted-foreground">
              {event.contribution_type}
            </Badge>
          )}
        </div>
        <h3 className="font-heading text-xl md:text-2xl font-medium text-foreground group-hover:text-[#5C6B5D] transition-colors duration-300 mb-2">
          {event.title}
        </h3>
        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground font-body mb-3">
          <span className="flex items-center gap-1">
            <Calendar size={12} strokeWidth={1.5} />
            {new Date(event.event_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </span>
          {event.location && (
            <span className="flex items-center gap-1">
              <MapPin size={12} strokeWidth={1.5} />
              {event.location}
            </span>
          )}
        </div>
        <p className="font-body text-sm text-muted-foreground leading-relaxed line-clamp-3">
          {event.short_description}
        </p>
        {event.organized_by && (
          <p className="font-body text-xs text-muted-foreground/60 mt-3">
            Organized by {event.organized_by}
          </p>
        )}
      </div>
    </Link>
  );

  return (
    <div data-testid="events-page">
      {/* Hero */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <FadeIn>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl tracking-tight font-light text-foreground">
              Events
            </h1>
            <p className="font-body text-base md:text-lg text-muted-foreground mt-6 max-w-xl mx-auto leading-relaxed">
              Living engagement through dialogue, retreat, and shared inquiry.
            </p>
          </div>
        </FadeIn>
      </section>

      {/* Filter */}
      <section className="px-6 md:px-12 lg:px-24 pb-8">
        <div className="max-w-screen-xl mx-auto">
          <FadeIn>
            <div className="flex items-center gap-4">
              <span className="font-body text-xs tracking-[0.15em] uppercase text-muted-foreground">Filter:</span>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[200px] bg-transparent border-[hsl(48,12%,88%)] rounded-none font-body text-sm" data-testid="event-type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#FDFCF8] border-[hsl(48,12%,88%)] rounded-none">
                  {EVENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t} className="font-body text-sm">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Upcoming */}
      <section className="py-12 md:py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-screen-xl mx-auto">
          <FadeIn>
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-8 text-foreground">Upcoming</h2>
          </FadeIn>
          {upcoming.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcoming.map((event, i) => (
                <FadeIn key={event.id} delay={i * 0.08}>
                  <EventCard event={event} index={i} />
                </FadeIn>
              ))}
            </div>
          ) : (
            <FadeIn>
              <p className="font-body text-sm text-muted-foreground italic">No upcoming events at the moment. Check back soon.</p>
            </FadeIn>
          )}
        </div>
      </section>

      <div className="px-6 md:px-12 lg:px-24">
        <div className="max-w-screen-xl mx-auto">
          <Separator style={{ backgroundColor: "#E5E4DE" }} />
        </div>
      </div>

      {/* Past */}
      <section className="py-12 md:py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-screen-xl mx-auto">
          <FadeIn>
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-8 text-muted-foreground">Past Events</h2>
          </FadeIn>
          {past.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {(showAllPast ? past : past.slice(0, 6)).map((event, i) => (
                  <FadeIn key={event.id} delay={i * 0.08}>
                    <EventCard event={event} index={i} />
                  </FadeIn>
                ))}
              </div>
              {past.length > 6 && !showAllPast && (
                <div className="text-center mt-12">
                  <button
                    onClick={() => setShowAllPast(true)}
                    className="font-body text-sm tracking-[0.15em] uppercase text-[#5C6B5D] hover:underline underline-offset-4 decoration-1 transition-all duration-300"
                    data-testid="load-more-past"
                  >
                    Load More
                  </button>
                </div>
              )}
            </>
          ) : (
            <FadeIn>
              <p className="font-body text-sm text-muted-foreground italic">No past events to show.</p>
            </FadeIn>
          )}
        </div>
      </section>
    </div>
  );
}
